import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./styles";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { getCampaignSubscriptionsForCampaign } from "redux/selector";
import { Campaign } from "model/campaign";
import { Table } from "component/shared/table";
import { CampaignSubscription } from "model/campaign-subscription";
import { getSubscriptionStats } from "redux/selector";
import { Lead } from "model/lead";
import * as Icons from "react-feather";
import { CampaignSubscriptionStatus } from "type/campaign-subscription-status";
import { AddSubscription } from "./add-subscription";
import { updateCampaignSubscription } from "redux/slice/campaign-subscriptions";
import { StatusMenu } from "./status-menu";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { urls } from "routes/urls";
import { CampaignSubscriptionStat } from "model/campaign-subscription-stat";
import * as env from "shared/env";

interface Props extends
	OwnProps,
	WithMediaQuery,
	WithStyles<typeof styles>
{
}

interface OwnProps {
	campaign: Campaign;
}

const Component: React.FC<Props> = ({ campaign, mediaQuery, classes }) => {
	const dispatch = useAppThunkDispatch();
	
	// Redux selectors
	const campaignSubscriptions = useSelector((state: RootState) => getCampaignSubscriptionsForCampaign(state, campaign.id));
	const campaignSubscriptionStats = useSelector(getSubscriptionStats);
	

	// Local state
	const [campaignSubscriptionsWithStats, setCampaignSubscriptionsWithStats] = useState<CampaignSubscription[]>([]);
	const [statusFilter, setStatusFilter] = useState(true);
	const [openAddNew, setOpenAddNew] = useState(false);
	const [subscriberFilter, setSubscriberFilter] = useState<string | undefined>(undefined);

	// Memoized function to get campaign subscription stat
	const getCampaignSubscriptionStat = useCallback((campaignSubscriptionId: number) => {
		return campaignSubscriptionStats.find((campaignSubscriptionStat: CampaignSubscriptionStat) => { 
			return campaignSubscriptionStat.campaignSubscription.id === campaignSubscriptionId;
		});
	}, [campaignSubscriptionStats]);

	// Function to add fields to subscription - memoized to prevent infinite loops
	const addFieldsToSubscription = useCallback(() => {
		let campaignSubscriptionsWithStatsLocal: CampaignSubscription[] = []; 		

		campaignSubscriptions.forEach((campaignSubscription: CampaignSubscription) => {
			const stat = getCampaignSubscriptionStat(campaignSubscription.id);
			const campaignSubscriptionWithStats: CampaignSubscription = {
				...campaignSubscription,
				stat: stat,
			}
			campaignSubscriptionsWithStatsLocal.push(campaignSubscriptionWithStats);
		});

		if (statusFilter) {
			campaignSubscriptionsWithStatsLocal = campaignSubscriptionsWithStatsLocal.filter(
				(campaignSubscription) => campaignSubscription.status === CampaignSubscriptionStatus.ACTIVE
			)
		}
		
		setCampaignSubscriptionsWithStats(campaignSubscriptionsWithStatsLocal);
	}, [campaignSubscriptions, getCampaignSubscriptionStat, statusFilter]);

	// Effect to update subscriptions when dependencies change
	useEffect(() => {
		addFieldsToSubscription();
	}, [addFieldsToSubscription]);

	// Memoized filtered subscriptions
	const filteredCampaignSubscriptionsWithStats = useMemo(() => {
		let campaignSubscriptionsLocal: CampaignSubscription[] = [...campaignSubscriptionsWithStats];
		const filterValue = subscriberFilter;
		if (filterValue) {
			return campaignSubscriptionsLocal.filter((subscription) => {
				return subscription.lead && subscription.lead.name && 
					subscription.lead.name.toLowerCase().includes(filterValue.toLowerCase());
			});		
		}
		return campaignSubscriptionsLocal;
	}, [campaignSubscriptionsWithStats, subscriberFilter]);

	// Memoized subscribed leads
	const subscribedLeads = useMemo(() => {
		const leadsLocal: Lead[] = [];
		campaignSubscriptionsWithStats.forEach((campaignSubscription) => {
			if (campaignSubscription.lead) {
				leadsLocal.push(campaignSubscription.lead);
			}
		});
		return leadsLocal;
	}, [campaignSubscriptionsWithStats]);

	const handleStatusFilter = useCallback(() => {
		setStatusFilter(!statusFilter);
	}, [statusFilter]);

	const handleAdd = useCallback(() => {
		setOpenAddNew(true);
	}, []);

	const updateStatus = useCallback((campaignSubscription: CampaignSubscription, campaignSubscriptionStatus: CampaignSubscriptionStatus) => {
		campaignSubscription.status = campaignSubscriptionStatus;
		dispatch(updateCampaignSubscription({subscription: campaignSubscription}));		
	}, [dispatch]);

	const renderToolsLeft = useCallback(() => {
		return (
			<>
				<Mui.Grid container direction="row" alignItems="center" wrap="nowrap">
					<Mui.Grid item> 
						<Mui.Button
							variant="contained"
							color={"primary"}
							onClick={handleAdd}
						>
							Add New
						</Mui.Button>
					</Mui.Grid>
					<Mui.Grid item style={{paddingLeft: 20}}>						
						<Mui.Button 
							aria-controls="simple-menu"
							aria-haspopup="true"
							onClick={handleStatusFilter}
							style={{textAlign: "center"}}							
						>
							{statusFilter ? (
								<>
									Showing Active
									<Icons.Maximize2 size={14} style={{marginLeft: 5}} />
								</>
							) : (
								<>
									Showing All
									<Icons.Minimize2 size={14} style={{marginLeft: 5}} />
								</>
							)}
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>
			</>
		);
	}, [handleAdd, handleStatusFilter, statusFilter]);

	const renderToolsRight = useCallback(() => {
		return (
			<>
				<Mui.Grid
					container
					direction="column"
					alignItems="flex-end" 
					style={{marginTop: mediaQuery ? 15 : undefined}} 
					justifyContent={mediaQuery ? "flex-start" : "flex-end"}
					wrap="nowrap"
				>
					<Mui.Grid item>
						<Mui.Grid item>
							<Mui.TextField
								placeholder="Search Subscribers"
								onChange={(element) => setSubscriberFilter(element.target.value)}
							/>
						</Mui.Grid>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Typography color="textSecondary">*Data provided only within the last 30 days is shown</Mui.Typography>
					</Mui.Grid>
				</Mui.Grid>
			</>
		);
	}, [mediaQuery]);

	return (
		<>
			<AddSubscription 
				open={openAddNew} 
				onClose={() => setOpenAddNew(false)} 
				subscribedLeads={subscribedLeads} 
				campaign={campaign}
			/>
			<Mui.Grid container direction="column" spacing={1} style={{marginTop: 15}}>
				<Mui.Grid item>					
					<Mui.Grid container direction="row" alignItems="center">
						<Mui.Grid item xs={12} md={6}>
							{renderToolsLeft()}
						</Mui.Grid>
						<Mui.Grid item xs={12} md={6}>
							{renderToolsRight()}
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item>
					<Table 
						items={filteredCampaignSubscriptionsWithStats}
						columns={[
							{
								id: "Subscriber",
								title: "Subscriber",
								sort: (value1, value2) => {
									let direction = 1;
									if ((value1.lead && value1.lead.name) && (value2.lead && value2.lead.name)) {
										if (value1.lead.name.toLowerCase() < value2.lead.name.toLowerCase()) {
											direction = -1;
										}
									}
									return direction;
								},
								value: (campaignSubscription) => {
									if (!campaignSubscription.lead) {
										return "Error Getting Name";
									}
									const url = urls.lead(campaignSubscription.lead)
									return (
										<Mui.Grid container direction="column">
											<Mui.Link component={RouterLinkWrapper} to={url}>
												{campaignSubscription.lead.label}
											</Mui.Link>						
										</Mui.Grid>
									);
								},
							},
							{
								id: "Added",
								title: "Added",
								sort: (value1, value2) => {
									return value1.createdOn.valueOf() < value2.createdOn.valueOf() ? -1 : 1;
								},
								value: (campaignSubscription) => {
									return (
										<Mui.Grid container direction="column">
											{campaignSubscription.createdOn.format("MM/DD/YYYY")}
										</Mui.Grid>
									);
								}
							},
							{ 
								id: "Status",
								title: "Status",
								sort: (value1, value2) => {
									return value1.status.label < value2.status.label ? -1 : 1;
								},
								value: (campaignSubscription) => {
									return (
										<Mui.Grid container direction="column" style={{alignContent: "flex-start"}}>
											<StatusMenu campaignSubscriptionStatus={campaignSubscription.status} onChange={(status) => updateStatus(campaignSubscription, status)} />
										</Mui.Grid>
									);
								}
							},
							{
								id: "Source",
								title: "Source",
								value: (campaignSubscription) => {
									return (
										<Mui.Grid container direction="column">
											{campaignSubscription.lead && campaignSubscription.lead.source ? campaignSubscription.lead.source : "N/A"}
										</Mui.Grid>
									);
								}
							},
							{
								id: "Opens",
								title: "Opens*",
								sort: (value1, value2) => {
									let direction = 1;
									if (value1.stat && value2.stat) {
										if (value1.stat.open < value2.stat.open) {
											direction = -1;
										}
									}
									return direction;
								},
								value: (campaignSubscription) => {
									return (
										<Mui.Grid container direction="column">
											{campaignSubscription.stat && campaignSubscription.stat.open ? campaignSubscription.stat.open : "N/A"}
										</Mui.Grid>
									);
								}
							},
							{
								id: "Clicks",
								title: "Clicks*",
								sort: (value1, value2) => {
									let direction = 1;
									if (value1.stat && value2.stat) {
										if (value1.stat.click < value2.stat.click) {
											direction = -1;
										}
									}
									return direction;
								},
								value: (campaignSubscription) => {
									return (
										<Mui.Grid container direction="column">
											{campaignSubscription.stat && campaignSubscription.stat.click ? campaignSubscription.stat.click : "N/A"}
										</Mui.Grid>
									);
								}
							},
							{
								id: "Stage",
								title: "Stage*",
								sort: (value1, value2) => {
									let direction = 1;
									if (value1.stat && value2.stat) {
										if (value1.stat.step < value2.stat.step) {
											direction = -1;
										}
									}
									return direction;
								},
								value: (campaignSubscription) => {
									return (
										<Mui.Grid container direction="column">
											{campaignSubscription.stat && campaignSubscription.stat.step ? campaignSubscription.stat.step : "N/A"}
										</Mui.Grid>
									);
								}
							},
							{
								id: "sent",
								title: "Last Sent*",
								sort: (value1, value2) => {
									let direction = 1;
									if ((value1.stat && value2.stat) && (value1.stat.lastSent && value2.stat.lastSent)) {
										if (value1.stat.lastSent.valueOf() < value2.stat.lastSent.valueOf()) {
											direction = -1;
										}
									}
									return direction;
								},
								value: (campaignSubscription) => {
									return (
										<Mui.Grid container direction="column">
											{campaignSubscription.stat && campaignSubscription.stat.lastSent ? campaignSubscription.stat.lastSent.format("MM/DD/YYYY") : "N/A"}
										</Mui.Grid>
									);
								}
							},
						]}
					/>
				</Mui.Grid>	
			</Mui.Grid>
		</>
	);
};

export const Subscribers = withMediaQuery("(max-width:600px)")(withStyles(Component, styles));